/*
 * Inbox v2, the client master inbox (`docs/2026-10-16-inbox-v2-build-plan.md`, card 5). It is a page
 * inside the app shell (master-inbox plan, step 2: docs/2026-10-17-master-inbox-in-the-existing-ui-plan.md):
 * the shell's rail carries its boxes and label views, and this draws the list and the reading sheet;
 * on a phone the list, and the thread over it, under the shell's top bar.
 *
 * Calm reading, fast hands: one conversation in focus, and every action has a key (J, K move; X picks
 * a row; E Done; U seen or unseen; L relabel; R reply; / search; Esc clears). Keys are never shown on
 * a touch screen. Everything here sits behind the `inbox` entitlement, which
 * the API enforces; the browser only chooses what to draw. State and handlers: `use-inbox-page.ts`.
 */
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { type BackfillCampaign, summarizeBackfill } from "../../src/backfill-progress.ts";
import type { InboxAgentState } from "../../src/inbox-v2/agent-contract.ts";
import { LIST, resizeList } from "../../src/pane-layout.ts";
import { useAgentStates } from "../agent/use-agent.ts";
import { FiltersIcon } from "../icons.tsx";
import { PaneDivider } from "../pane-divider.tsx";
import type { Shell } from "../shell/app-shell.tsx";
import { useTabTitle } from "../shell/tab-title.ts";
import { useArrivals } from "./effects.ts";
import { HeldToasts, useHeldSends } from "./held-sends.tsx";
import { ImportNote } from "./import-note.tsx";
import { FilterChips, personName } from "./list.tsx";
import {
  type FoldedPanel,
  foldedPanelOf,
  ListBody,
  ListHeading,
  SelectionBar,
  SelectionStatus,
  ToastView,
  ViewMenu,
} from "./list-column.tsx";
import type { EmptyContext } from "./list-empty.tsx";
import { ReadingPane } from "./reading-pane.tsx";
import { SaveView } from "./saved-views.tsx";
import { coverageNote, type SearchCoverage, searchCoverage } from "./search-coverage.ts";
import { SearchField } from "./search-field.tsx";
import { snoozeMenuAt } from "./snooze-menu.tsx";
import {
  useImportProgress,
  useInboxAccess,
  usePrefetchNeighbours,
  useSearchStatus,
} from "./use-inbox.ts";
import { type InboxPageModel, undoToast, useInboxPage } from "./use-inbox-page.ts";
import { statusPlace } from "./use-list-controls.ts";
import { suggestedName } from "./view-query.ts";
import { useSelectWhole, WholeListOffer } from "./whole-list.tsx";
import {
  useWorkspaceImport,
  type WorkspaceImport,
  WorkspaceImportNote,
} from "./workspace-import-note.tsx";

/**
 * The selection bar for the ticked rows. On a desktop it takes the heading line's place and stays
 * there while the list scrolls (sticky), so a run longer than a screen keeps its count and actions in
 * view; on a phone it docks at the foot and comes after the rows, as a screen reader meets it.
 */
/**
 * "Select all N in this list" under the bar, once every row on the page is ticked and the list holds
 * more (`whole-list.tsx`); a list still loading offers nothing, as its rows are the list before.
 */
function WholeListLine(props: { page: InboxPageModel }) {
  const { selection, data } = props.page;
  const select = useSelectWhole(data.listKey, data.readPage, selection.holdWhole);
  const listPage = data.list.page;
  const total = listPage?.total ?? 0;
  if (!listPage || data.list.placeholder || selection.whole || !selection.allShown) return null;
  if (total <= data.visible.length) return null;
  return (
    <WholeListOffer
      total={total}
      reading={select.reading}
      failed={select.failed}
      onSelect={() => void select.start(total, listPage.limit)}
    />
  );
}

function ListSelectionBar(props: { page: InboxPageModel }) {
  const { page } = props;
  const { selection, data } = page;
  if (!page.selecting) return null;
  const ticked = selection.whole ?? data.visible.filter((row) => selection.selected.has(row.id));
  const labels = new Set(ticked.map((row) => row.label?.code ?? null));
  const total = data.list.page?.total ?? data.visible.length;
  return (
    <div className="lcr-bulk-stick">
      <SelectionBar
        count={selection.selected.size}
        wholePage={selection.allShown && total > data.visible.length}
        wholeList={selection.whole !== null}
        header={header(page)}
        showKeys={page.showKeys}
        relabelOpen={page.bulkRelabel}
        relabelCurrent={labels.size === 1 ? [...labels][0] : undefined}
        snoozed={page.box === "snoozed"}
        snoozeMenu={snoozeMenuAt(page, "bar")}
        onToggleAll={selection.allShown ? selection.clear : selection.all}
        onDone={() => void page.done()}
        inTrash={page.box === "trash"}
        onTrash={() => void page.trash()}
        onSeen={(value) => page.seen(undefined, value)}
        onRelabelOpen={page.setBulkRelabel}
        onRelabel={(label) => void page.relabel(label)}
        onSnooze={() => page.snoozeMenu(undefined, "bar")}
        onUnsnooze={() => void page.wake()}
        onClear={selection.stop}
        assign={
          page.assigning.assignment && {
            assignment: page.assigning.assignment,
            open: page.assigning.bulk,
            onOpenChange: page.assigning.setBulk,
            onChoose: (value) => void page.assigning.assign(value),
          }
        }
      />
      <WholeListLine page={page} />
    </div>
  );
}

/**
 * A selection that ends (×, Esc, the bar's box) takes its bar, and the focus with it: the focus goes
 * back to the list's one Tab stop rather than to the page, so the keyboard knows where it is.
 */
function useFocusAfterSelection(selecting: boolean) {
  const was = useRef(selecting);
  useEffect(() => {
    const ended = was.current && !selecting;
    was.current = selecting;
    const at = document.activeElement;
    if (!ended || (at && at !== document.body)) return;
    document.querySelector<HTMLElement>('.lcr-i2 .lcr-list .lcr-row[tabindex="0"]')?.focus();
  }, [selecting]);
}

/** How much of the page the selection holds, for the bar's checkbox. */
function header(page: InboxPageModel): "none" | "some" | "all" {
  const { selection } = page;
  if (selection.allShown) return "all";
  return selection.selected.size > 0 ? "some" : "none";
}

/** How many filters narrow the list: the chips that are on, for the phone's Filters button. */
function filtersOn(list: InboxPageModel["list"]): number {
  const { filters, search } = list;
  const chips = [filters.status, filters.agent, filters.unread, filters.days, filters.check];
  const tokens = search.tokens.filter(
    (token) => token.kind === "campaign-id" || token.kind === "has",
  );
  return chips.filter((chip) => chip !== null && chip !== false).length + tokens.length;
}

/** A window too short for the chips beside the rows: 200% zoom of a 900 px one is 450 px. */
const SHORT_WINDOW = "(max-height: 599px)";

function useShortWindow(): boolean {
  return useSyncExternalStore(
    (changed) => {
      const query = window.matchMedia?.(SHORT_WINDOW);
      query?.addEventListener("change", changed);
      return () => query?.removeEventListener("change", changed);
    },
    () => window.matchMedia?.(SHORT_WINDOW).matches ?? false,
  );
}

/**
 * On a phone the filter chips fold behind one Filters button beside the search (design review #12):
 * two rows of chips put the first conversation 279 px down. They show while opened, and while any
 * is on, so a narrowed list always shows what narrows it. A short window folds them the same way
 * (critique, LEA-501: at 200% zoom four rows of chips left 1.5 conversations on screen).
 */
function usePhoneFilters(page: InboxPageModel) {
  const [open, setOpen] = useState<boolean | null>(null);
  const short = useShortWindow();
  const folds = page.phone || short;
  const on = filtersOn(page.list);
  const shown = !folds || (open ?? on > 0);
  return { folds, shown, on, toggle: () => setOpen(!shown) };
}
type PhoneFilters = ReturnType<typeof usePhoneFilters>;

function SearchRow(props: { page: InboxPageModel; filters: PhoneFilters }) {
  const { page, filters } = props;
  const { search } = page.list;
  return (
    <div className="lcr-i2-search-row">
      <SearchField
        ref={page.searchField}
        workspaceId={page.workspaceId}
        tokens={search.tokens}
        draft={search.draft}
        campaigns={page.data.counts.data?.campaigns ?? []}
        showKeys={page.showKeys}
        onDraft={search.setDraft}
        onChoose={search.choose}
        onRemove={search.remove}
        onCommit={search.commit}
        onClear={search.clear}
      />
      {filters.folds && (
        <button
          type="button"
          className="lcr-i2-filters-toggle"
          aria-expanded={filters.shown}
          onClick={filters.toggle}
        >
          <FiltersIcon size={18} />
          Filters
          {filters.on > 0 && <span className="lcr-num"> {filters.on}</span>}
        </button>
      )}
    </div>
  );
}

/** "Save view" while the list is narrowed, or "Save changes" while a saved view has some. */
function SaveViewControl(props: { page: InboxPageModel }) {
  const { page } = props;
  const { list } = page;
  const narrowed = list.filtered && page.choice.kind !== "view";
  // A view never keeps a reply agent status, so it is not offered while one narrows the list.
  if ((!narrowed && !list.changedView) || page.agentStatus !== null) return null;
  return (
    <SaveView
      workspaceId={page.workspaceId}
      query={list.viewQuery}
      suggestedName={suggestedName(
        list.search.tokens,
        list.search.draft,
        list.shown,
        page.data.counts.data?.campaigns ?? [],
        page.name,
      )}
      changed={list.changedView}
      saved={page.data.views.length}
      onSaved={(id) => page.choose({ kind: "view", id })}
    />
  );
}

/** The Campaign filter's choices: on their way, known, or not known at all (the counts failed). */
const filterCampaigns = (counts: InboxPageModel["data"]["counts"]) =>
  counts.data?.campaigns ?? (counts.isLoading ? "loading" : null);

/**
 * The heading's total: a bar while the list is on its way, the rows on screen included when they are
 * the list shown before (`useKeptList`), whose total is another list's; none after a failure.
 */
function shownTotal(list: InboxPageModel["data"]["list"]): number | "pending" | undefined {
  if (list.failed || list.refused) return undefined;
  return list.placeholder || !list.page ? "pending" : list.page.total;
}

/*
 * The list's header, the reply agent's own (`main.tsx`): find, then the filters, then the heading
 * line, which the bulk bar replaces while rows are ticked; the bar folds to the column it is given
 * (`SelectionBar`).
 */
function ListHead(props: { page: InboxPageModel; tabs: boolean; folded: FoldedPanel | null }) {
  const { page } = props;
  const { selection, data, list } = page;
  const counts = data.counts.data;
  const filters = usePhoneFilters(page);
  return (
    <>
      <SearchRow page={page} filters={filters} />
      <SelectionStatus selecting={page.selecting} count={selection.selected.size} />
      <fieldset className="lcr-show" aria-label="Show conversations">
        {filters.shown && (
          <FilterChips
            filters={list.filters}
            tokens={list.search.tokens}
            onTokens={list.search.setTokens}
            campaigns={filterCampaigns(data.counts)}
            counts={counts}
            countsPending={data.counts.isLoading}
            status={!statusPlace(page.choice)}
            label={page.choice.kind !== "label"}
            checkCount={counts?.check}
            agent={page.replyAgent ? { counts: data.list.agentCounts } : undefined}
            onChange={page.setFilters}
          />
        )}
        <ListHeading
          name={list.q ? `Results in ${page.name}` : page.name}
          total={shownTotal(data.list)}
          /*
           * On a phone a saved view and a scope (Mine, Unassigned, a colleague) are named by the
           * heading: no tab stands for them, so the tabs would name the list wrongly. With the
           * inbox's panel folded on a desktop the tabs are its rows, so they stay on every list but
           * a search's: More names the box, view or folder, and Showing the scope (LEA-501).
           */
          tabs={
            props.tabs &&
            !list.q &&
            (props.folded !== null ||
              (page.box === "inbox" && page.choice.kind !== "view" && page.owner === null))
          }
          folded={props.folded ?? undefined}
          choice={page.choice}
          counts={counts}
          onChoose={page.choose}
          menu={
            <>
              <SaveViewControl page={page} />
              <ViewMenu
                sort={page.agentStatus === null ? list.sort : "newest"}
                newestOnly={page.agentStatus !== null}
                mode={list.mode}
                onSort={list.setSort}
                onMode={list.setMode}
                {...(page.phone ? { onSelect: () => selection.startSelecting() } : {})}
              />
              {snoozeMenuAt(page, "head")}
            </>
          }
        />
      </fieldset>
    </>
  );
}

/**
 * The list is one Tab stop (step 10, round 2): Tab lands on the row the keyboard is on, else the open
 * one, else the first, and goes on from there to the thread. J and K move within it from anywhere;
 * the arrows do too while a row has the focus, and with Shift they tick as they go, as Shift+J/K do
 * (the WAI-ARIA listbox's Shift+Arrow).
 */
function rovingRow(page: InboxPageModel): string | null {
  const ids = page.data.list.page?.conversations.map((row) => row.id) ?? [];
  const at = [page.focusedId, page.openId].find((id) => id !== null && ids.includes(id));
  return at ?? ids[0] ?? null;
}

function arrows(page: InboxPageModel) {
  return (event: React.KeyboardEvent<HTMLElement>) => {
    // From a row, or from a row's box, which keeps the focus after a click.
    const from = event.target instanceof HTMLElement ? event.target : null;
    if (!from?.matches(".lcr-row, .lcr-row-check input")) return;
    const step = event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : null;
    if (step === null) return;
    event.preventDefault();
    if (event.shiftKey) page.extend(step);
    else page.move(step);
  };
}

/**
 * What an empty list says and offers (`list-empty.tsx`). "Search all of Inbox" keeps the words and
 * takes the rest away; it is offered from a box or a label, not from the Inbox, where it would only
 * be Remove filters again, nor from a saved view, whose words leave with it.
 */
function emptyContext(
  page: InboxPageModel,
  imports: readonly BackfillCampaign[] | undefined,
  workspaceImport: WorkspaceImport,
  coverage: SearchCoverage | null,
): EmptyContext {
  const { list, choice } = page;
  const counts = page.data.counts.data;
  const words = list.search.draft.trim();
  const elsewhere = choice.kind === "label" || (choice.kind === "box" && choice.box !== "inbox");
  return {
    narrowing: {
      filtered: list.filtered,
      searched: list.search.draft !== "",
      narrowed: list.narrowed,
      boxName: page.name,
    },
    choice,
    words,
    coverage,
    totals: counts && {
      meeting: counts.meeting,
      needsReply: counts.needs_reply,
      inbox: counts.all,
    },
    importing: imports ? summarizeBackfill(imports) : null,
    workspaceImporting: workspaceImport.running,
    onRemoveFilters: page.removeFilters,
    onClearSearch: list.search.clear,
    owner: page.owner,
    onEveryone: page.everyone,
    onSearchInbox:
      words && elsewhere
        ? () => {
            page.removeFilters();
            page.choose({ kind: "box", box: "inbox" });
          }
        : null,
    onChoose: page.choose,
  };
}

function ListColumn(props: {
  page: InboxPageModel;
  tabs: boolean;
  folded: FoldedPanel | null;
  /** The reply agent's state per row, for a person with the switch on; empty otherwise. */
  agent: ReadonlyMap<string, InboxAgentState | null>;
}) {
  const { page, agent } = props;
  const { data, selection } = page;
  // Why the list may be incomplete while a campaign's history imports: only agent holders onboard.
  const imports = useImportProgress(page.workspaceId, page.replyAgent);
  // Why it may be incomplete while the workspace's own mail imports: said to everyone, and first.
  const workspaceImport = useWorkspaceImport(page.workspaceId);
  // A typed search reads the index, which can lag the list: say so rather than look complete.
  const searched = page.list.q !== "";
  const coverage = searchCoverage(searched, useSearchStatus(page.workspaceId, searched));
  useFocusAfterSelection(page.selecting);
  return (
    <aside
      className="lcr-list"
      aria-label={page.name}
      data-selecting={page.selecting || undefined}
      onKeyDown={arrows(page)}
    >
      <ListHead page={page} tabs={props.tabs} folded={props.folded} />
      {!page.phone && <ListSelectionBar page={page} />}
      <WorkspaceImportNote workspaceImport={workspaceImport} />
      {!workspaceImport.line && imports.data && <ImportNote campaigns={imports.data} />}
      {page.box === "trash" && (
        <p className="lcr-list-coverage lcr-i2-trash-note" role="note">
          Trash is emptied automatically after 7 days.
        </p>
      )}
      <ListBody
        main={data.list.page}
        stale={data.list.placeholder}
        mainFailed={data.list.failed}
        failureReference={data.list.reference}
        retrying={data.list.fetching}
        refused={data.list.refused}
        strip={data.strip}
        empty={emptyContext(page, imports.data, workspaceImport, coverage)}
        coverage={coverageNote(coverage)}
        sort={page.list.sort}
        offset={page.list.offset}
        rows={{
          showCampaign: new Set(data.visible.map((row) => row.campaign.id)).size > 1,
          focusedId: page.focusedId,
          openId: page.openId,
          tabbableId: rovingRow(page),
          selected: selection.selected,
          selecting: page.selecting,
          tapTicks: page.tapTicks,
          onOpen: page.open,
          onFocus: page.focusRow,
          onToggle: page.toggleRow,
          onLongPress: selection.startSelecting,
          // Rows kept from the list before answer an older question: read, never acted on (W4).
          readOnly: data.list.placeholder,
          // Where a pointer hovers: a touch screen snoozes from the bulk bar after Select.
          onSnooze: page.showKeys ? (row) => page.snoozeMenu([row], { row: row.id }) : null,
          snoozeMenu: (row) => snoozeMenuAt(page, { row: row.id }),
          agentState: (id) => agent.get(id) ?? null,
          you: page.assigning.assignment?.you ?? null,
        }}
        onRetry={data.list.refetch}
        onPage={page.setOffset}
        onClearAgent={() => page.setFilters({ ...page.list.filters, agent: null })}
        onSeeAll={(place) => page.choose({ kind: place })}
      />
      {page.phone && <ListSelectionBar page={page} />}
    </aside>
  );
}

/** The layout's switches as data attributes; an absent attribute is off. */
/** A list narrower than this, the default 400px among them, draws the Unanswered clock short. */
const NARROW_LIST_PX = 440;

function layoutFlags(page: InboxPageModel) {
  const on = (value: boolean) => value || undefined;
  return {
    "data-phone": on(page.phone),
    "data-thread": on(page.openId !== null),
    "data-keys": on(page.showKeys),
  };
}

/**
 * Leaving with unsaved work is asked first (the shell's guard), whichever part holds it: words the
 * reply box could not keep, or a reply agent draft edited and not saved.
 */
function useUnsavedParts(setUnsaved: (dirty: boolean) => void) {
  const [reply, setReply] = useState(false);
  const [draft, setDraft] = useState(false);
  useEffect(() => setUnsaved(reply || draft), [reply, draft, setUnsaved]);
  return { reply: setReply, draft: setDraft };
}

/**
 * The reply agent's part of the page, for a person whose switch is on (`/access`): the state on each
 * row on screen, and on the open conversation, which a search or a filter may have taken off the
 * list: its item's chip is its own (step 10, round 1), asked for beside the rows within the route's limit. Off, nothing is asked (`use-agent.ts`) and
 * every row and thread draws as a client rep's does.
 */
function useAgentPart(page: InboxPageModel) {
  const access = useInboxAccess(page.workspaceId);
  const enabled = access.data?.replyAgent === true;
  // While nothing is open, the reading pane's next reply names its agent state too (`nothing-open.tsx`).
  const offered = page.openId === null ? (page.shortlist.data ?? []) : [];
  const rows = [...(page.data.strip?.rows ?? []), ...page.data.visible, ...offered].map(
    (row) => row.id,
  );
  // Only once the list is in: before it, the open row is asked about with the rest, not alone. Asked
  // apart from the rows, so the route's limit holds with a full page and an open conversation from
  // off it, and opening one leaves the rows' request as it was.
  const away = page.openId !== null && page.data.list.page && !rows.includes(page.openId);
  const groups = away && page.openId ? [rows, [page.openId]] : [rows];
  return { enabled, states: useAgentStates(page.workspaceId, groups, enabled) };
}

/**
 * The master inbox as the shell's page. While the rail is collapsed to icons its rows under Inbox
 * become tabs above the list, and on a phone too, where the rail is gone: there they would otherwise
 * wait behind the workspace pill's sheet (design review #12).
 */
export function InboxV2Page(props: { shell: Shell }) {
  const { shell } = props;
  const page = useInboxPage(shell.current.id, {
    phone: shell.onPhone,
    userId: shell.user.id,
    choice: shell.mailChoice,
    owner: shell.owner,
    go: shell.leave,
    guarded: shell.guarded,
  });
  const held = useHeldSends();
  const unsaved = useUnsavedParts(shell.setUnsaved);
  const reply = useMemo(
    () => ({ userId: shell.user.id, onHeld: held.add, onUnsaved: unsaved.reply }),
    [shell.user.id, held.add, unsaved.reply],
  );
  const agent = useAgentPart(page);
  // The tab names the open conversation as its heading does (`shell/tab-title.ts`).
  const opened = page.data.thread.data?.conversation;
  useTabTitle(opened && opened.id === page.openId ? personName(opened) : null);
  // New mail and other people's changes, while this inbox stays open (W5): a small read, and the
  // lists read again only when the workspace moved.
  useArrivals(page.workspaceId, true);
  usePrefetchNeighbours(
    page.workspaceId,
    page.data.visible,
    { id: page.openId, loaded: page.data.thread.isSuccess },
    agent.enabled,
    page.assigning.assignment !== null,
  );
  // The tabs name the list on a phone, and stand in for the inbox's panel where it has folded.
  const folded = foldedPanelOf(shell);
  const tabs = shell.onPhone || folded !== null;
  const { toast, setToast } = page;
  const { layout, fitted, changeLayout } = shell;
  // The list column's width is the shell's fitted layout, shared with the reply agent's list.
  const widths: Record<string, string> = { "--lcr-list-w": `${fitted.list}px` };
  // Below this the row's Unanswered clock gives up its word for its mark (styles.css); a phone's
  // list is the screen, so there a media query decides.
  const narrowList = !shell.onPhone && fitted.list < NARROW_LIST_PX;
  return (
    <main
      className="lcr-main lcr-i2"
      style={widths}
      data-narrow-list={narrowList || undefined}
      {...layoutFlags(page)}
    >
      {/* The page's own heading for a screen reader: the view, and whose inbox (design review #15). */}
      <h1 className="lcr-visually-hidden">
        {page.name}, {shell.current.name}
      </h1>
      <PaneDivider
        label="Resize conversation list"
        className="lcr-divider-list"
        value={fitted.list}
        min={LIST.min}
        max={LIST.max}
        fromPointer={(x) =>
          x - (document.querySelector(".lcr-i2")?.getBoundingClientRect().left ?? 0)
        }
        onResize={(width, done) => changeLayout(resizeList(layout, width), done)}
      />
      <ListColumn page={page} tabs={tabs} folded={folded} agent={agent.states} />
      <ReadingPane page={page} reply={reply} agent={{ ...agent, onUnsaved: unsaved.draft }} />
      <ToastView
        toast={toast}
        onUndo={() => undoToast(toast, setToast)}
        onHold={page.holdToast}
        onAction={() => {
          setToast(null);
          toast?.action?.run();
        }}
      >
        <HeldToasts
          workspaceId={page.workspaceId}
          holds={held.holds}
          openId={page.openId}
          onDrop={held.drop}
          onOpen={page.reopen}
        />
      </ToastView>
    </main>
  );
}
